import { useEffect, useState } from 'react';
import './Balsuite.css';
import logo from '../assets/logo.png';
import companyLogo from '../assets/balvirt.png';

// ─── SCROLL REVEAL HOOK ───────────────────────────────────────────────────────
export function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    if (!els.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          } else {
            entry.target.classList.remove('in-view');
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// ─── ICONS ───────────────────────────────────────────────────────────────────
function Icon({ name, size = 18 }) {
  const p = {
    userPlus: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></>,
    clock: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>,
    folder: <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />,
    listCheck: <><path d="m3 17 2 2 4-4" /><path d="m3 7 2 2 4-4" /><path d="M13 6h8" /><path d="M13 12h8" /><path d="M13 18h8" /></>,
    barChart: <><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></>,
    zap: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
    layers: <><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>,
    dashboard: <><rect width="7" height="9" x="3" y="3" rx="1" /><rect width="7" height="5" x="14" y="3" rx="1" /><rect width="7" height="9" x="14" y="12" rx="1" /><rect width="7" height="5" x="3" y="16" rx="1" /></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    building: <><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" /><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" /><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" /><path d="M10 6h4" /><path d="M10 10h4" /><path d="M10 14h4" /><path d="M10 18h4" /></>,
    folderPlus: <><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /><line x1="12" y1="11" x2="12" y2="17" /><line x1="9" y1="14" x2="15" y2="14" /></>,
    timer: <><line x1="10" y1="2" x2="14" y2="2" /><path d="M12 14V8" /><path d="M4.93 10.93a10 10 0 1 0 14.14 0" /></>,
    rocket: <><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" /><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" /><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" /><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" /></>,
    twitter: <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />,
    linkedin: <><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect width="4" height="12" x="2" y="9" /><circle cx="4" cy="4" r="2" /></>,
    youtube: <><path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" /><path d="m10 15 5-3-5-3z" /></>,
    github: <><path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" /><path d="M9 18c-4.51 2-5-2-7-2" /></>,
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
    globe: <><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>,
    trending: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></>,
    check: <polyline points="20 6 9 17 4 12" />,
    play: <><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" /></>,
    arrowRight: <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>,
    box: <><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.3 7 12 12 20.7 7" /><line x1="12" y1="22" x2="12" y2="12" /></>,
    bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p[name]}
    </svg>
  );
}

// ─── DATA ────────────────────────────────────────────────────────────────────
const features = [
  { icon: 'userPlus', title: 'Employee Onboarding', theme: 'indigo', wide: true, desc: 'Digitise the entire onboarding journey — documents, role setup, team introductions, and access provisioning — so every new hire is productive from day one.', tag: 'Day-1 ready' },
  { icon: 'clock', title: 'Timesheet Management', theme: 'violet', desc: 'Log hours with daily, weekly, or project-based timesheets. Auto-track billable vs non-billable with one-click submissions and approvals.', tag: 'Auto-tracking' },
  { icon: 'folder', title: 'Project Management', theme: 'cyan', desc: 'Plan, execute, and deliver on time. Milestones, dependencies, Gantt views, and real-time progress — all in one workspace.', tag: 'On-time delivery' },
  { icon: 'listCheck', title: 'Work Management', theme: 'emerald', desc: 'Assign tasks, set priorities, track deadlines, and collaborate in context. From individual to-dos to complex team workflows.', tag: 'Zero missed tasks' },
  { icon: 'barChart', title: 'Resource Utilization', theme: 'amber', desc: 'See exactly who is overloaded or available. Balance workloads across teams, prevent burnout, and maximise capacity intelligently.', tag: 'Capacity insights' },
  { icon: 'zap', title: 'Sprint Management', theme: 'indigo', wide: true, desc: 'Run agile sprints with ease. Plan backlogs, create sprint boards, track velocity, and ship iteratively with burndown charts and retrospectives built in.', tag: 'Agile-ready' },
  { icon: 'calendar', title: 'Leave Management', theme: 'rose', wide: true, desc: 'Real-time clock-in/out, shift scheduling, leave requests, and balance tracking — seamlessly linked to your projects and timesheets so nothing falls through the cracks.', tag: 'Always in sync' },
  { icon: 'layers', title: 'Asset Management', theme: 'violet', wide: true, desc: 'Track, allocate, and maintain company assets from procurement to retirement. Full lifecycle visibility with maintenance schedules, transfer logs, and audit trails.', tag: 'Full lifecycle' },
];

const steps = [
  { num: '01', title: 'Set up your workspace', desc: 'Create your organisation, invite your team, and configure departments and roles in under 10 minutes — no IT ticket needed.' },
  { num: '02', title: 'Create projects & assign work', desc: 'Build projects, break them into tasks, assign owners, set deadlines, and connect everything to sprints or timesheets instantly.' },
  { num: '03', title: 'Track progress in real time', desc: 'Live dashboards show where every project stands, how resources are allocated, and where blockers are forming — before they become problems.' },
  { num: '04', title: 'Optimise and ship faster', desc: 'Use utilisation reports, sprint velocity trends, and performance nudges to continuously improve how your team works.' },
];

const flowNodes = [
  { icon: 'building', label: 'Create Workspace', hint: 'Org · Departments · Teams' },
  { icon: 'folderPlus', label: 'Add Projects', hint: 'Milestones · Phases · Goals' },
  { icon: 'listCheck', label: 'Assign Tasks & Sprints', hint: 'Owners · Deadlines · Priority' },
  { icon: 'timer', label: 'Log Time & Track Work', hint: 'Timesheets · Attendance · Progress' },
  { icon: 'rocket', label: 'Ship & Improve', hint: 'Analytics · Velocity · Retrospectives' },
];

const testimonials = [
  { text: '"Balsuite has completely changed how I manage my sprints and daily tasks. The timesheet integration with project milestones means I never have to manually reconcile hours again. It feels built for developers — not managers trying to micromanage us."', name: 'Prashant Darade', role: 'Senior Software Developer', initials: 'PD', accent: '#6366f1' },
  { text: '"Tracking test cycles, logging defects, and linking them back to sprint tasks used to be a nightmare across three tools. With Balsuite, everything is in one place. I can see exactly which builds are blocked and communicate status to the team in real time."', name: 'Rahul Bidgar', role: 'Senior QA Engineer', initials: 'RB', accent: '#8b5cf6', featured: true },
  { text: '"Onboarding used to take a week of back-and-forth emails. Balsuite cut that down to a single day. Leave requests, attendance, and employee records are all connected — I spend less time chasing approvals and more time actually supporting the team."', name: 'Jyoti Khodake', role: 'HR Assistant', initials: 'JK', accent: '#0891b2' },
];

const navItems = [
  { icon: 'dashboard', label: 'Dashboard', badge: null, active: true },
  { icon: 'folder', label: 'Projects', badge: '4' },
  { icon: 'listCheck', label: 'My Tasks', badge: '8' },
  { icon: 'zap', label: 'Sprints', badge: '2' },
  { icon: 'clock', label: 'Timesheets', badge: null },
  { icon: 'box', label: 'Assets Management', badge: null },
  { icon: 'bell', label: 'Announcements', badge: 2 },
  { icon: 'users', label: 'Resources', badge: null },
  { icon: 'calendar', label: 'Leave Management', badge: '3' },
];

const kpis = [
  { label: 'Active Projects', val: '48', trend: '↑ 6 this week', color: '#6366f1' },
  { label: 'Sprint Velocity', val: '87%', trend: '↑ 4% vs last sprint', color: '#8b5cf6' },
  { label: 'Team Utilized', val: '78%', trend: 'Optimal range', color: '#0891b2' },
  { label: 'Tasks In Progress', val: '312', trend: '24 resolved today', color: '#059669' },
];

const progressBars = [
  { name: 'Balsuite v2.0', pct: 72, color: '#6366f1' },
  { name: 'Mobile App', pct: 45, color: '#8b5cf6' },
  { name: 'API Gateway', pct: 88, color: '#0891b2' },
];

const sprintBars = [62, 78, 55, 90, 72, 85, 94, 87, 91, 97];
const teamRows = [
  { name: 'Arjun N.', project: 'Balsuite v2.0', hours: '6.5h', status: 'active', bg: '#6366f1' },
  { name: 'Sneha I.', project: 'Mobile App', hours: '4.0h', status: 'remote', bg: '#8b5cf6' },
  { name: 'Pooja N.', project: 'Design System', hours: '7.2h', status: 'active', bg: '#0891b2' },
];

const socials = [
  { icon: 'twitter', label: 'X / Twitter' },
  { icon: 'linkedin', label: 'LinkedIn' },
  { icon: 'youtube', label: 'YouTube' },
  { icon: 'github', label: 'GitHub' },
];

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

export function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    window.addEventListener('scroll', close, { passive: true });
    return () => window.removeEventListener('scroll', close);
  }, [menuOpen]);

  const links = [
    { label: 'Features', href: '/#features' },
    { label: 'Solutions', href: '/#how-it-works' },
    { label: 'Why Balsuite', href: '/why-balsuite' },
    { label: 'Blog', href: '/blog' },
  ];

  return (
    <nav>
      <div className="nav-inner">

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img src={logo} alt="Balsuite" className="nav-logo" />
          </a>
          <div style={{ width: '1px', height: '16px', background: 'rgba(0,0,0,0.15)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', opacity: 0.7 }}>by</span>
            <a href="https://balvirt.com" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <img src={companyLogo} alt="balvirt" className="nav-company-logo" />
            </a>
          </div>
        </div>

        <div className="nav-links">
          {links.map(l => <a key={l.label} href={l.href}>{l.label}</a>)}
        </div>

        <div className="nav-cta">
          <a href="/login" style={{ display: 'contents' }}><button className="btn-ghost">Log in</button></a>
          <a href="/contact" style={{ display: 'contents' }}><button className="btn-nav-grad">Get started free →</button></a>
        </div>

        <button
          className={`nav-hamburger${menuOpen ? ' open' : ''}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Toggle navigation"
          aria-expanded={menuOpen}
        >
          <span /><span /><span />
        </button>
      </div>

      <div className={`nav-mobile-menu${menuOpen ? ' open' : ''}`}>
        {links.map(l => (
          <a key={l.label} href={l.href} onClick={() => setMenuOpen(false)}>{l.label}</a>
        ))}
        <div className="nav-mobile-cta">
          <a href="/login" onClick={() => setMenuOpen(false)}>
            <button className="btn-ghost" style={{ width: '100%' }}>Log in</button>
          </a>
          <a href="/contact" onClick={() => setMenuOpen(false)}>
            <button className="btn-nav-grad" style={{ width: '100%', textAlign: 'center' }}>Get started free →</button>
          </a>
        </div>
      </div>
    </nav>
  );
}

function HeroDashboard() {
  return (
    <div className="hd-wrap">
      <div className="hd-topbar">
        <div className="hd-dots">
          <span className="hd-dot r" /><span className="hd-dot y" /><span className="hd-dot g" />
        </div>
        <div className="hd-url">app.balsuite.io/dashboard</div>
        <div className="hd-live"><span className="hd-live-dot" />Live</div>
      </div>
      <div className="hd-body">
        <div className="hd-sidebar">
          <div className="hd-nav-label">Workspace</div>
          {navItems.map(n => (
            <div className={`hd-nav-item${n.active ? ' active' : ''}`} key={n.label}>
              <span className="hd-nav-icon"><Icon name={n.icon} size={13} /></span>
              {n.label}
              {n.badge && <span className="hd-badge">{n.badge}</span>}
            </div>
          ))}
        </div>
        <div className="hd-main">
          <div className="hd-kpi-row">
            {kpis.map(k => (
              <div className="hd-kpi" key={k.label}>
                <div className="hd-kpi-label">{k.label}</div>
                <div className="hd-kpi-val" style={{ color: k.color }}>{k.val}</div>
                <div className="hd-kpi-trend">{k.trend}</div>
              </div>
            ))}
          </div>
          <div className="hd-mid">
            <div className="hd-box">
              <div className="hd-box-title">Sprint Velocity</div>
              <div className="hd-bars">
                {sprintBars.map((h, i) => (
                  <div key={i}
                    className={`hd-bar${h >= 88 ? ' hi' : h >= 75 ? ' mid' : ''}`}
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
              <div className="hd-bar-labels">
                {['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8', 'S9', 'S10'].map((d, i) => (
                  <span key={i}>{d}</span>
                ))}
              </div>
            </div>
            <div className="hd-box">
              <div className="hd-box-title">Project Progress</div>
              {progressBars.map(p => (
                <div className="hd-prog-row" key={p.name}>
                  <div className="hd-prog-info">
                    <div className="hd-prog-dot" style={{ background: p.color }} />
                    <span className="hd-prog-name">{p.name}</span>
                  </div>
                  <div className="hd-prog-track">
                    <div className="hd-prog-fill" style={{ width: `${p.pct}%`, background: p.color }} />
                  </div>
                  <span className="hd-prog-pct">{p.pct}%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="hd-table">
            <div className="hd-table-hd">
              <span>Member</span><span>Project</span><span>Hours</span><span>Status</span>
            </div>
            {teamRows.map(r => (
              <div className="hd-table-row" key={r.name}>
                <div className="hd-cell-name">
                  <div className="hd-avi" style={{ background: r.bg }}>{r.name[0]}</div>
                  <span>{r.name}</span>
                </div>
                <div className="hd-cell">{r.project}</div>
                <div className="hd-cell">{r.hours}</div>
                <div className="hd-cell">
                  <span className={`hd-pill ${r.status}`}>
                    {r.status === 'active' ? 'Active' : 'Remote'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="hero">
      <div className="hero-orb-1" /><div className="hero-orb-2" />
      <div className="hero-inner">
        <div className="hero-left">
          {/* Badge — CSS keyframe animation (badgePop) */}
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            Asset Management &amp; HR Analytics — live soon
            <span className="hero-badge-arrow"><Icon name="arrowRight" size={12} /></span>
          </div>

          {/* h1, desc, actions, stats — staggered CSS fadeUp */}
          <h1>
            One platform for<br />
            <span className="grad-text">every team,<br />every task.</span>
          </h1>
          <p className="hero-desc">
            <strong>Balsuite</strong> unifies projects, sprints, timesheets, leave, assets, and
            resources into one intelligent workspace — so your entire company runs from a single place.
          </p>
          <div className="hero-actions">
            <a href="/contact" style={{ display: 'contents' }}><button className="btn-gradient">Start for free →</button></a>
            <a href="#demo" style={{ display: 'contents' }}><button className="btn-glass">
              <span className="play-wrap"><Icon name="play" size={13} /></span>
              Watch demo
            </button></a>
          </div>
          <div className="hero-stats">
            {[{ val: '10+', label: 'Teams powered' }, null, { val: '2k+', label: 'Tasks completed' }, null, { val: '99.9%', label: 'Uptime SLA' }]
              .map((s, i) => s === null
                ? <div className="hstat-sep" key={i} />
                : <div className="hstat" key={s.val}>
                  <div className="hstat-val">{s.val}</div>
                  <div className="hstat-label">{s.label}</div>
                </div>
              )}
          </div>
        </div>

        {/* Dashboard — CSS fadeRight + floating idle */}
        <div className="hero-right">
          <HeroDashboard />
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section className="features" id="features">
      <div className="features-inner">
        {/* Section header — scroll reveal */}
        <div className="features-hd">
          <div className="reveal from-left">
            <span className="sec-label">Everything you need</span>
            <h2 className="sec-h2">
              Every module your team needs,<br />
              <span className="grad-text">all connected</span>
            </h2>
          </div>
          <p className="sec-sub reveal from-right">
            From onboarding your first hire to shipping your hundredth sprint — Balsuite connects
            every part of how your company works, automatically.
          </p>
        </div>

        {/* Cards — staggered scroll reveal */}
        <div className="feat-bento">
          {features.map((f, i) => (
            <div
              className={`feat-card${f.wide ? ' wide' : ''} reveal delay-${(i % 4) + 1}`}
              key={f.title}
            >
              <div className={`feat-icon theme-${f.theme}`}>
                <Icon name={f.icon} size={20} />
              </div>
              <div className="feat-content">
                <div className="feat-top">
                  <h3 className="feat-title">{f.title}</h3>
                  <span className={`feat-tag theme-${f.theme}`}>{f.tag}</span>
                </div>
                <p className="feat-desc">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="how" id="how-it-works">
      <div className="how-inner">
        <div className="how-left">
          <div className="reveal">
            <span className="sec-label">How it works</span>
            <h2 className="sec-h2">
              From setup to shipping<br />
              <span className="grad-text">in minutes</span>
            </h2>
            <p className="sec-sub">
              No lengthy onboarding. No consultants. Your entire work management system goes live
              in a single afternoon.
            </p>
          </div>

          <div className="how-steps" style={{ marginTop: '3rem' }}>
            {steps.map((s, i) => (
              <div
                className={`how-step reveal delay-${i + 1}`}
                key={s.num}
              >
                <div className="step-num">{s.num}</div>
                <div className="step-body" style={{ paddingBottom: i === steps.length - 1 ? 0 : undefined }}>
                  <div className="step-title">{s.title}</div>
                  <p className="step-desc">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Workflow card — scroll reveal from right */}
        <div className="how-right reveal from-right delay-2">
          <div className="how-card">
            <div className="how-card-label">Workflow Setup</div>
            <div className="pf-list">
              {flowNodes.map((n, i) => (
                <div key={i}>
                  <div className="pf-node">
                    <div className="pf-ico"><Icon name={n.icon} size={15} /></div>
                    <div>
                      <div className="pf-label">{n.label}</div>
                      <div className="pf-hint">{n.hint}</div>
                    </div>
                    <div className="pf-check"><Icon name="check" size={10} /></div>
                  </div>
                  {i < flowNodes.length - 1 && <div className="pf-line" />}
                </div>
              ))}
            </div>
            <div className="how-card-footer">
              {[{ val: '10 min', lbl: 'Avg. setup' }, null, { val: '40%', lbl: 'Faster delivery' }, null, { val: '5+', lbl: 'Tools replaced' }]
                .map((s, i) => s === null
                  ? <div className="hcf-sep" key={i} />
                  : <div className="hcf-stat" key={s.lbl}>
                    <div className="hcf-val">{s.val}</div>
                    <div className="hcf-lbl">{s.lbl}</div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="testimonials" id="testimonials">
      <div className="testi-inner">
        {/* Header */}
        <div className="testi-hd">
          <div className="reveal from-left">
            <span className="sec-label">Testimonials</span>
            <h2 className="sec-h2">Teams work <span className="grad-text">better</span> with Balsuite</h2>
          </div>
          <p className="sec-sub reveal from-right" style={{ maxWidth: '260px' }}>
            Over 1+ teams worldwide use Balsuite to simplify their work and ship results faster.
          </p>
        </div>

        {/* Cards — staggered reveal */}
        <div className="testi-grid">
          {testimonials.map((t, i) => (
            <div
              className={`testi-card${t.featured ? ' featured' : ''} reveal delay-${i + 1}`}
              key={t.name}
            >
              <div className="testi-stars">{'★'.repeat(5)}</div>
              <p className="testi-text">{t.text}</p>
              <div className="testi-author">
                <div className="testi-avi" style={{ background: t.accent }}>{t.initials}</div>
                <div>
                  <div className="testi-name">{t.name}</div>
                  <div className="testi-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="cta" id="get-started">
      <div className="cta-inner">
        {/* entire block reveals as one unit, then children animate via CSS */}
        <div className="reveal scale-up">
          <div className="cta-badge">
            <Icon name="trending" size={13} />
            Join 1+ team already shipping faster
          </div>
          <h2>
            Ready to simplify how<br />
            <span className="grad-text">your team works?</span>
          </h2>
          <p className="cta-sub">
            Cut the chaos, unify your tools, and give every person in your company a clearer
            way to work — starting today.
          </p>
          <div className="cta-btns">
            <a href="/contact" style={{ display: 'contents' }}><button className="btn-gradient btn-lg">Start for free →</button></a>
            <a href="#demo" style={{ display: 'contents' }}><button className="btn-glass-cta">See a live demo</button></a>
          </div>
          {/* <p className="cta-note">Free plan available · No credit card required · Cancel anytime</p> */}
          <div className="cta-trust">
            {[
              // { icon: 'shield', label: 'SOC 2 Compliant' },
              // { icon: 'globe',  label: 'GDPR Ready'      },
              // { icon: 'check',  label: '99.9% Uptime'    },
              // { icon: 'zap',    label: 'Free Migrations'  },
            ].map(f => (
              <div className="cta-trust-item" key={f.label}>
                <span className="cta-trust-icon"><Icon name={f.icon} size={13} /></span>
                {f.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  useReveal();
  const cols = [
    {
      heading: 'Product', links: [
        { label: 'Features', href: '/#features' },
        { label: 'Pricing', href: '/pricing' },
        { label: 'Changelog', href: '/changelog' },
        { label: 'Roadmap', href: '/roadmap' },
        { label: 'Integrations', href: '/#how-it-works' },
      ]
    },
    {
      heading: 'Company', links: [
        { label: 'Why Balsuite', href: '/why-balsuite' },
        { label: 'About Us', href: '/about' },
        { label: 'Careers', href: '/careers' },
        { label: 'Blog', href: '/blog' },
        { label: 'Press', href: '/press' },
      ]
    },
    {
      heading: 'Support', links: [
        { label: 'Help Centre', href: '/help' },
        { label: 'Contact Us', href: '/contact' },
        { label: 'Status', href: '/status' },
        { label: 'Community', href: '/community' },
        { label: 'Security', href: '/security' },
      ]
    },
  ];
  return (
    <footer>
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-brand reveal from-left">
            <a href="/"><img src={logo} alt="Balsuite" className="nav-logo" /></a>
            <p className="footer-tagline">
              Simplify Work, Amplify Results. The smarter work management platform for your entire company.
            </p>
            <div className="footer-social">
              {socials.map(s => (
                <a href="#" className="soc-btn" key={s.label} aria-label={s.label} rel="noopener noreferrer">
                  <Icon name={s.icon} size={15} />
                </a>
              ))}
            </div>
          </div>
          {cols.map((col, i) => (
            <div className={`footer-col reveal delay-${i + 2}`} key={col.heading}>
              <h4>{col.heading}</h4>
              <ul>{col.links.map(l => <li key={l.label}><a href={l.href}>{l.label}</a></li>)}</ul>
            </div>
          ))}
        </div>
        <div className="footer-bottom">

          <div className="footer-copy" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src={companyLogo} alt="balvirt" style={{ height: '16px', opacity: 0.9 }} />
            <span>© {new Date().getFullYear()} balvirt. All rights reserved.</span>
          </div>
          <div className="footer-legal">
            {[
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '#' },
              { label: 'Cookie Policy', href: '/cookies' },
            ].map(l => (
              <a href={l.href} key={l.label}>{l.label}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function BalsuiteApp() {
  useReveal(); // 👈 wires up IntersectionObserver for all .reveal elements

  return (
    <div className="balsuite-page">
      <Navbar />
      <Hero />
      {/* <Logos /> */}
      <Features />
      <HowItWorks />
      <Testimonials />
      <CTA />
      <Footer />
    </div>
  );
}